import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function Popover(_ref) {
  let {
    id = '',
    children = undefined,
    className = '',
    childrenClassName = '',
    wrapperClassName = '',
    content = '',
    dark = false,
    simpleMode = false,
    position = 'top',
    showArrow = false,
    trigger = 'click',
    width = '',
    onClick = undefined,
    isVisible = undefined
  } = _ref;
  const [show, setShow] = useState(false);
  const wrapperRef = useRef(null);
  const childRef = useRef(null);
  const tooltipRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0
  });
  useEffect(() => {
    if (isVisible !== undefined) {
      setShow(isVisible);
    }
  }, [isVisible]);
  const handleMouseEnter = () => {
    if (trigger === 'hover' && isVisible === undefined || simpleMode) {
      setShow(true);
    }
  };
  const handleMouseLeave = () => {
    if (trigger === 'hover' && isVisible === undefined || simpleMode) {
      setShow(false);
    }
  };
  const handleOnClick = e => {
    if (!simpleMode && isVisible === undefined) {
      setShow(!show);
    }
    onClick && onClick(e);
  };
  useEffect(() => {
    if (simpleMode && show && childRef.current && tooltipRef.current) {
      const childRect = childRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: childRect.top - tooltipRect.height - 10,
        left: childRect.left + (childRect.width - tooltipRect.width) / 2
      });
    }
  }, [simpleMode, show]);
  useEffect(() => {
    if (!simpleMode && show) {
      const childElement = document.getElementById(`popover-${id}-trigger-element`);
      if (!childElement) return;
      const {
        offsetHeight: childHeight,
        offsetWidth: childWidth
      } = childElement;
      const popover = document.getElementById(`popover-${id}-container`);
      if (!popover) return;
      const {
        offsetHeight: popoverHeight
      } = popover;
      const arrow = document.getElementById(`popover-arrow-${id}`);
      let arrowHeight;
      let arrowWidth;
      if (showArrow && arrow) {
        arrowHeight = arrow.offsetHeight;
        arrowWidth = arrow.offsetWidth;
      }
      const offset = showArrow ? 10 : 0;
      const containerMargin = 10;
      const arrowMargin = 4;
      switch (position) {
        case 'top':
          popover.style.bottom = `${childHeight + offset + containerMargin}px`;
          if (showArrow && arrow) {
            arrow.style.bottom = `${childHeight + offset + arrowMargin}px`;
          }
          break;
        case 'top-start':
          popover.style.bottom = `${childHeight + offset + containerMargin}px`;
          popover.style.left = `-${containerMargin}px`;
          if (showArrow && arrow) {
            arrow.style.bottom = `${childHeight + offset + arrowMargin}px`;
            arrow.style.left = `${arrowMargin}px`;
          }
          break;
        case 'top-end':
          popover.style.bottom = `${childHeight + offset + containerMargin}px`;
          popover.style.right = `-${containerMargin}px`;
          if (showArrow && arrow) {
            arrow.style.bottom = `${childHeight + offset + arrowMargin}px`;
            arrow.style.right = `${arrowMargin}px`;
          }
          break;
        case 'right':
          popover.style.left = `${childWidth + offset + containerMargin}px`;
          popover.style.bottom = `${childHeight / 2 - popoverHeight / 2}px`;
          if (showArrow && arrow) {
            arrow.style.left = `${childWidth + offset + arrowMargin}px`;
            arrow.style.bottom = `${childHeight / 2 - arrowHeight / 2}px`;
          }
          break;
        case 'bottom':
          popover.style.top = `${childHeight + offset + containerMargin}px`;
          if (showArrow && arrow) {
            arrow.style.top = `${childHeight + offset + arrowMargin}px`;
          }
          break;
        case 'bottom-start':
          popover.style.top = `${childHeight + containerMargin}px`;
          popover.style.left = `-${containerMargin}px`;
          if (showArrow && arrow) {
            arrow.style.top = `${childHeight + arrowMargin}px`;
            arrow.style.left = `${arrowMargin}px`;
          }
          break;
        case 'bottom-end':
          popover.style.top = `${childHeight + containerMargin}px`;
          popover.style.right = `-${containerMargin}px`;
          if (showArrow && arrow) {
            arrow.style.top = `${childHeight + arrowMargin}px`;
            arrow.style.right = `${arrowMargin}px`;
          }
          break;
        case 'left':
          popover.style.right = `${childWidth + offset + containerMargin}px`;
          popover.style.bottom = `${childHeight / 2 - popoverHeight / 2}px`;
          if (showArrow && arrow) {
            arrow.style.right = `${childWidth + offset + arrowMargin}px`;
            arrow.style.bottom = `${childHeight / 2 - arrowHeight / 2}px`;
          }
          break;
        default:
      }
    }
  }, [id, position, show, showArrow, simpleMode]);
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShow(false);
      }
    }
    if (show && !simpleMode) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [show, simpleMode]);
  const getBaseContentStyles = () => cn('border border-solid p-4', {
    'border-light-border-blue rounded bg-white': !dark,
    'rounded bg-text-blue text-white': dark
  }, className, `${width ? `w-${width}` : ''}`);
  const containerClass = () => cn('absolute z-popover', {
    'invisible hidden': !show
  });
  const contentClass = () => getBaseContentStyles();
  const arrowClass = () => cn('h-3 w-3 border-b border-solid border-r border-light-border-blue transform absolute z-popover', {
    'bg-white': !dark,
    'bg-text-blue': dark,
    'rotate-45': position.includes('top'),
    '-rotate-135': position.includes('bottom'),
    'rotate-135': position === 'right',
    '-rotate-45': position === 'left',
    invisible: !show
  });
  const wrapperClass = () => cn('relative flex flex-shrink-0', {
    'justify-center': !position.includes('start') && !position.includes('end'),
    'w-full': simpleMode
  }, wrapperClassName);
  const simpleTooltipClasses = cn('fixed z-popover', getBaseContentStyles(), {
    'opacity-0 pointer-events-none': !show,
    'opacity-100': show
  });
  if (simpleMode) {
    return /*#__PURE__*/_jsxs("div", {
      className: "relative inline-block",
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ref: wrapperRef,
      children: [/*#__PURE__*/_jsx("div", {
        ref: childRef,
        className: "w-full",
        children: children
      }), show && /*#__PURE__*/_jsx("div", {
        ref: tooltipRef,
        className: simpleTooltipClasses,
        style: {
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          maxWidth: '300px',
          wordWrap: 'break-word',
          whiteSpace: 'normal'
        },
        children: content
      })]
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    id: id,
    "data-testid": id,
    ref: wrapperRef,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    className: wrapperClass(),
    children: [/*#__PURE__*/_jsx("div", {
      className: containerClass(),
      id: `popover-${id}-container`,
      "data-testid": `popover-${id}-container`,
      children: /*#__PURE__*/_jsx("div", {
        className: contentClass(),
        "data-testid": "popover-content",
        children: /*#__PURE__*/_jsx("span", {
          id: `popover-${id}-content`,
          children: content
        })
      })
    }), showArrow && /*#__PURE__*/_jsx("div", {
      id: `popover-arrow-${id}`,
      className: arrowClass(),
      "data-testid": `popover-arrow-${id}`
    }), /*#__PURE__*/_jsx("div", {
      id: `popover-${id}-trigger-element`,
      className: childrenClassName,
      "data-testid": `popover-${id}-trigger-element`,
      onClick: handleOnClick,
      children: children
    })]
  });
}
Popover.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  childrenClassName: PropTypes.string,
  wrapperClassName: PropTypes.string,
  content: PropTypes.node,
  dark: PropTypes.bool,
  simpleMode: PropTypes.bool,
  position: PropTypes.oneOf(['top', 'top-start', 'top-end', 'right', 'bottom', 'bottom-start', 'bottom-end', 'left']),
  showArrow: PropTypes.bool,
  trigger: PropTypes.oneOf(['hover', 'click']),
  width: PropTypes.string,
  onClick: PropTypes.func,
  isVisible: PropTypes.bool,
  children: PropTypes.node
};
export default Popover;
//# sourceMappingURL=Popover.js.map